// =============================================================================
// Scheduler Store - Zustand State Management
// =============================================================================

import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import { startOfDay, addDays, subDays } from 'date-fns';
import { getTenantTimeZone } from './tenantStore';
import { civilDayToCarrier, todayInTz } from '../lib/tenantTime';

/**
 * "Oggi" secondo il calendario dell'organizzazione, come carrier di data civile
 * (vedi lib/tenantTime). Per un tenant in un fuso lontano il giorno corrente
 * puo' essere diverso da quello del browser dell'operatore.
 */
const tenantToday = () => civilDayToCarrier(todayInTz(getTenantTimeZone()));

// ===========================================================================
// Types
// ===========================================================================

/**
 * Ampiezza dell'intervallo mostrato dalla vista agenda. La griglia risorse
 * resta sempre giornaliera: qui l'unita' e' quella di navigazione dell'elenco.
 * `upcoming` non e' un intervallo ma un elenco aperto da oggi in avanti,
 * scaricato a pagine.
 */
export type ViewMode = 'day' | 'week' | 'month' | 'upcoming';
/** I modi con confini calcolabili: tutti tranne l'elenco aperto sul futuro. */
export type BoundedViewMode = Exclude<ViewMode, 'upcoming'>;
/** Come sono disegnati gli appuntamenti: griglia risorse o elenco cronologico. */
export type CalendarView = 'grid' | 'agenda';
export type BookingPanelMode = 'drawer' | 'modal';

export interface SelectedSlot {
  start: Date;
  end?: Date;
  resourceId?: string;
  resourceName?: string;
}

/** Anteprima appuntamento fantasma sulla griglia */
export interface PreviewAppointment {
  scheduledStart: string;
  scheduledEnd: string;
  serviceName: string;
  serviceColor: string | null;
  customerName: string | null;
  allocations: Array<{ resourceId: string; resourceName: string }>;
}

/** I tre filtri della griglia, nella forma in cui viaggiano da e verso l'API. */
export interface FilterPresetState {
  resourceTypeIds: string[] | null;
  resourceIds: string[];
  searchTerm: string;
}

interface SchedulerState {
  // State
  selectedDate: Date;
  /**
   * Tipi risorsa da mostrare. `null` = nessun filtro attivo (tutti i tipi,
   * compresi quelli creati dopo); un array elenca esattamente i tipi visibili,
   * `[]` compreso (nessun tipo, calendario vuoto). La distinzione fra "tutti"
   * e "tutti selezionati esplicitamente" serve a far si' che un tipo nuovo
   * compaia da solo finche' l'utente non ha toccato il filtro.
   */
  selectedResourceTypes: string[] | null;
  selectedResourceIds: string[];
  resourceSearchTerm: string;  // Ricerca testuale risorse
  /**
   * Vista salvata attualmente applicata, se ce n'e' una. E' solo un'etichetta:
   * i filtri veri restano nei campi qui sopra, quindi toccare un filtro non
   * "rompe" nulla — il menu confronta i due stati e segnala la divergenza.
   */
  activeFilterPresetId: string | null;
  calendarView: CalendarView;  // Griglia risorse vs agenda cronologica
  viewMode: ViewMode;          // Ampiezza dell'intervallo in vista agenda
  sidebarOpen: boolean;
  showBufferZones: boolean;  // Mostra zone buffer prima/dopo appuntamenti
  gridStartHour: number;    // Ora inizio griglia (default 8)
  gridEndHour: number;      // Ora fine griglia (default 20)
  useCustomTimeRange: boolean; // Se true usa la fascia custom, se false giornata intera (0-24)
  /// Mostra anche l'orario locale di chi guarda, accanto a quello
  /// dell'organizzazione. Ha effetto solo quando i due fusi divergono.
  showViewerLocalTimes: boolean;
  
  // Booking Panel State (side panel / modal)
  bookingPanelMode: BookingPanelMode;
  isModalOpen: boolean;
  selectedSlot: SelectedSlot | null;
  previewAppointment: PreviewAppointment | null;
  /** ID dell'evento di gruppo a cui l'utente sta scegliendo di aggregarsi (evidenziato sulla griglia) */
  joinTargetAppointmentId: string | null;

  // Modal State (Edit)
  isEditModalOpen: boolean;
  selectedAppointmentId: string | null;
  
  // Actions
  setDate: (date: Date) => void;
  goToNextDay: () => void;
  goToPrevDay: () => void;
  goToToday: () => void;
  
  /** `allTypeIds` e' l'universo dei tipi: serve a espandere lo stato "tutti". */
  toggleResourceType: (typeId: string, allTypeIds: string[]) => void;
  setResourceTypes: (typeIds: string[] | null) => void;
  
  toggleResource: (resourceId: string) => void;
  setResources: (resourceIds: string[]) => void;

  applyFilterPreset: (presetId: string | null, filters: FilterPresetState) => void;
  setActiveFilterPresetId: (presetId: string | null) => void;
  
  setShowViewerLocalTimes: (show: boolean) => void;

  setSearchTerm: (term: string) => void;  // Nuova azione
  toggleBufferZones: () => void;
  
  setCalendarView: (view: CalendarView) => void;
  setViewMode: (mode: ViewMode) => void;
  toggleSidebar: () => void;
  
  setGridTimeRange: (startHour: number, endHour: number) => void;
  toggleCustomTimeRange: () => void;
  
  resetFilters: () => void;
  
  // Booking Panel Actions (Create)
  setBookingPanelMode: (mode: BookingPanelMode) => void;
  openModal: (slot?: SelectedSlot) => void;
  closeModal: () => void;
  setPreviewAppointment: (preview: PreviewAppointment | null) => void;
  setJoinTargetAppointmentId: (id: string | null) => void;
  
  // Modal Actions (Edit)
  openEditModal: (appointmentId: string) => void;
  closeEditModal: () => void;
}

// ===========================================================================
// Store
// ===========================================================================

export const useSchedulerStore = create<SchedulerState>()(
  devtools(
    persist(
      (set) => ({
        // Initial State
        selectedDate: tenantToday(),
        selectedResourceTypes: null,
        selectedResourceIds: [],
        resourceSearchTerm: '',
        activeFilterPresetId: null,
        calendarView: 'grid',
        viewMode: 'day',
        sidebarOpen: true,
        showBufferZones: false,
        gridStartHour: 8,
        gridEndHour: 20,
        useCustomTimeRange: true,
        showViewerLocalTimes: false,
        
        // Modal Initial State (Create)
        bookingPanelMode: 'drawer' as BookingPanelMode,
        isModalOpen: false,
        selectedSlot: null,
        previewAppointment: null,
        joinTargetAppointmentId: null,

        // Modal Initial State (Edit)
        isEditModalOpen: false,
        selectedAppointmentId: null,

        // Date Actions
        setDate: (date: Date) => set({ 
          selectedDate: startOfDay(date) 
        }, false, 'setDate'),

        goToNextDay: () => set((state) => ({ 
          selectedDate: addDays(state.selectedDate, 1) 
        }), false, 'goToNextDay'),

        goToPrevDay: () => set((state) => ({ 
          selectedDate: subDays(state.selectedDate, 1) 
        }), false, 'goToPrevDay'),

        goToToday: () => set({ 
          selectedDate: tenantToday() 
        }, false, 'goToToday'),

        setShowViewerLocalTimes: (show: boolean) =>
          set({ showViewerLocalTimes: show }, false, 'setShowViewerLocalTimes'),

        // Resource Type Filters
        // Il click su un tipo agisce solo su quel tipo: se il filtro non era
        // attivo si parte da "tutti selezionati" e si toglie il tipo cliccato,
        // invece di trasformare il click in "solo questo".
        toggleResourceType: (typeId: string, allTypeIds: string[]) => set((state) => {
          const current = state.selectedResourceTypes ?? allTypeIds;
          const newTypes = current.includes(typeId)
            ? current.filter((id) => id !== typeId)
            : [...current, typeId];
          // Tornati a coprire tutti i tipi: si torna a "nessun filtro".
          return {
            selectedResourceTypes:
              newTypes.length === allTypeIds.length ? null : newTypes,
          };
        }, false, 'toggleResourceType'),

        setResourceTypes: (typeIds: string[] | null) => set({ 
          selectedResourceTypes: typeIds 
        }, false, 'setResourceTypes'),

        // Individual Resource Filters
        toggleResource: (resourceId: string) => set((state) => {
          const current = state.selectedResourceIds;
          const newIds = current.includes(resourceId)
            ? current.filter((id) => id !== resourceId)
            : [...current, resourceId];
          return { selectedResourceIds: newIds };
        }, false, 'toggleResource'),

        setResources: (resourceIds: string[]) => set({ 
          selectedResourceIds: resourceIds 
        }, false, 'setResources'),

        // Viste salvate: applicare un preset e' un'assegnazione secca dei tre
        // filtri, cosi' quello che si vede coincide sempre con cio' che e'
        // stato salvato.
        applyFilterPreset: (presetId: string | null, filters: FilterPresetState) => set({
          selectedResourceTypes: filters.resourceTypeIds,
          selectedResourceIds: filters.resourceIds,
          resourceSearchTerm: filters.searchTerm,
          activeFilterPresetId: presetId,
        }, false, 'applyFilterPreset'),

        setActiveFilterPresetId: (presetId: string | null) => set({
          activeFilterPresetId: presetId,
        }, false, 'setActiveFilterPresetId'),

        // Search Term
        setSearchTerm: (term: string) => set({ 
          resourceSearchTerm: term 
        }, false, 'setSearchTerm'),

        // Buffer Zones
        toggleBufferZones: () => set((state) => ({
          showBufferZones: !state.showBufferZones
        }), false, 'toggleBufferZones'),

        // View Actions
        setCalendarView: (view: CalendarView) => set({
          calendarView: view
        }, false, 'setCalendarView'),

        setViewMode: (mode: ViewMode) => set({
          viewMode: mode 
        }, false, 'setViewMode'),

        toggleSidebar: () => set((state) => ({ 
          sidebarOpen: !state.sidebarOpen 
        }), false, 'toggleSidebar'),

        // Grid Time Range
        setGridTimeRange: (startHour: number, endHour: number) => set({
          gridStartHour: Math.max(0, Math.min(23, startHour)),
          gridEndHour: Math.max(1, Math.min(24, endHour)),
        }, false, 'setGridTimeRange'),

        toggleCustomTimeRange: () => set((state) => ({
          useCustomTimeRange: !state.useCustomTimeRange
        }), false, 'toggleCustomTimeRange'),

        // Reset
        resetFilters: () => set({
          selectedResourceTypes: null,
          selectedResourceIds: [],
          resourceSearchTerm: '',
          activeFilterPresetId: null,
        }, false, 'resetFilters'),
        
        // Booking Panel Actions (Create)
        setBookingPanelMode: (mode: BookingPanelMode) => set({
          bookingPanelMode: mode,
        }, false, 'setBookingPanelMode'),

        openModal: (slot?: SelectedSlot) => set({
          isModalOpen: true,
          selectedSlot: slot || null,
        }, false, 'openModal'),
        
        closeModal: () => set({
          isModalOpen: false,
          selectedSlot: null,
          previewAppointment: null,
          joinTargetAppointmentId: null,
        }, false, 'closeModal'),

        setPreviewAppointment: (preview: PreviewAppointment | null) => set({
          previewAppointment: preview,
        }, false, 'setPreviewAppointment'),

        setJoinTargetAppointmentId: (id: string | null) => set({
          joinTargetAppointmentId: id,
        }, false, 'setJoinTargetAppointmentId'),
        
        // Modal Actions (Edit)
        openEditModal: (appointmentId: string) => set({
          isEditModalOpen: true,
          selectedAppointmentId: appointmentId,
        }, false, 'openEditModal'),
        
        closeEditModal: () => set({
          isEditModalOpen: false,
          selectedAppointmentId: null,
        }, false, 'closeEditModal'),
      }),
      {
        name: 'scheduler-store',
        version: 1,
        // v0 usava `[]` per dire "nessun filtro sui tipi"; ora quel valore
        // significa "nessun tipo visibile" e il filtro assente e' `null`.
        migrate: (persisted: unknown, version: number) => {
          const state = persisted as Partial<SchedulerState> | undefined;
          if (
            state &&
            version === 0 &&
            Array.isArray(state.selectedResourceTypes) &&
            state.selectedResourceTypes.length === 0
          ) {
            return { ...state, selectedResourceTypes: null };
          }
          return state;
        },
        partialize: (state) => ({
          // Only persist these fields
          selectedResourceTypes: state.selectedResourceTypes,
          selectedResourceIds: state.selectedResourceIds,
          activeFilterPresetId: state.activeFilterPresetId,
          calendarView: state.calendarView,
          viewMode: state.viewMode,
          sidebarOpen: state.sidebarOpen,
          showBufferZones: state.showBufferZones,
          gridStartHour: state.gridStartHour,
          gridEndHour: state.gridEndHour,
          useCustomTimeRange: state.useCustomTimeRange,
          showViewerLocalTimes: state.showViewerLocalTimes,
          bookingPanelMode: state.bookingPanelMode,
          // Don't persist selectedDate - always start with today
        }),
      }
    ),
    { name: 'SchedulerStore' }
  )
);

// ===========================================================================
// Selector Hooks (for performance optimization)
// ===========================================================================

export const useSelectedDate = () => useSchedulerStore((state) => state.selectedDate);
export const useShowViewerLocalTimes = () =>
  useSchedulerStore((state) => state.showViewerLocalTimes);
export const useSelectedResourceTypes = () => useSchedulerStore((state) => state.selectedResourceTypes);
export const useSelectedResourceIds = () => useSchedulerStore((state) => state.selectedResourceIds);
export const useResourceSearchTerm = () => useSchedulerStore((state) => state.resourceSearchTerm);
export const useActiveFilterPresetId = () => useSchedulerStore((state) => state.activeFilterPresetId);
export const useCalendarView = () => useSchedulerStore((state) => state.calendarView);
export const useViewMode = () => useSchedulerStore((state) => state.viewMode);
export const useSidebarOpen = () => useSchedulerStore((state) => state.sidebarOpen);
export const useShowBufferZones = () => useSchedulerStore((state) => state.showBufferZones);
export const useGridTimeRange = () => useSchedulerStore((state) => ({
  gridStartHour: state.useCustomTimeRange ? state.gridStartHour : 0,
  gridEndHour: state.useCustomTimeRange ? state.gridEndHour : 24,
  customStartHour: state.gridStartHour,
  customEndHour: state.gridEndHour,
  useCustomTimeRange: state.useCustomTimeRange,
}));

// Modal Selectors (Create / Booking Panel)
export const useBookingPanelMode = () => useSchedulerStore((state) => state.bookingPanelMode);
export const useIsModalOpen = () => useSchedulerStore((state) => state.isModalOpen);
export const useSelectedSlot = () => useSchedulerStore((state) => state.selectedSlot);
export const usePreviewAppointment = () => useSchedulerStore((state) => state.previewAppointment);
export const useJoinTargetAppointmentId = () => useSchedulerStore((state) => state.joinTargetAppointmentId);

// Modal Selectors (Edit)
export const useIsEditModalOpen = () => useSchedulerStore((state) => state.isEditModalOpen);
export const useSelectedAppointmentId = () => useSchedulerStore((state) => state.selectedAppointmentId);

// Actions (these don't cause re-renders)
export const useSchedulerActions = () => useSchedulerStore((state) => ({
  setDate: state.setDate,
  goToNextDay: state.goToNextDay,
  goToPrevDay: state.goToPrevDay,
  goToToday: state.goToToday,
  toggleResourceType: state.toggleResourceType,
  setResourceTypes: state.setResourceTypes,
  toggleResource: state.toggleResource,
  setResources: state.setResources,
  applyFilterPreset: state.applyFilterPreset,
  setActiveFilterPresetId: state.setActiveFilterPresetId,
  setSearchTerm: state.setSearchTerm,
  setCalendarView: state.setCalendarView,
  setViewMode: state.setViewMode,
  toggleSidebar: state.toggleSidebar,
  toggleBufferZones: state.toggleBufferZones,
  setGridTimeRange: state.setGridTimeRange,
  toggleCustomTimeRange: state.toggleCustomTimeRange,
  resetFilters: state.resetFilters,
  setBookingPanelMode: state.setBookingPanelMode,
  openModal: state.openModal,
  closeModal: state.closeModal,
  setPreviewAppointment: state.setPreviewAppointment,
  setJoinTargetAppointmentId: state.setJoinTargetAppointmentId,
  openEditModal: state.openEditModal,
  closeEditModal: state.closeEditModal,
}));
